// src/pages/FailureOfPlanning.js
import React from "react";
import ReactMarkdown from "react-markdown";
import "./Infrastructure.theme.css";

/**
 * FailureOfPlanning.js
 * 
 * Markdown page summarizing:
 * "The Failure of Planning to Address the Urban Interface and Intermix Fire-Hazard Problems in the San Francisco Bay Area"
 * by Joe R. McBride and Jerry Kent (2019)
 */

const md = `# The Failure of Planning to Address the Urban Interface and Intermix Fire-Hazard Problems in the San Francisco Bay Area  
**Joe R. McBride (UC Berkeley) & Jerry Kent (East Bay Regional Park District)**  
*International Journal of Wildland Fire, Vol. 28, 2019*  

---

## Abstract  
*Fill in summary here:*  
- Overview of how post-fire planning has failed to address vegetation and fuel management issues in the Bay Area’s WUI.  
- Mention proposal for new state agency oversight.  

---

## Historical Context  
Areas of urban development in the San Francisco Bay Area are
flanked by areas of wildland vegetation and agriculture. This
juxtaposition results in urban interface and intermix zones
where residential housing is faced with wildland vegetation and
agricultural land or is commingled with open-space vegetation.
Fire has been an important part of the environment of these hills
since Native Americans migrated into the region about 6,000

years ago. Three fires of large proportion occurred in this urban-
intermix zone in the 20th century. They burned portions of

Berkeley in 1923, Oakland and Berkeley in 1991 and Santa Rosa
in 2017. Property damage caused by these fires resulted from
open-space areas supporting flammable vegetation, lack of fuel
mitigation in the interface and intermix zones, and exceptional
fire weather. Reports by various agencies after major fires
offered recommendations for improving fire suppression and
mitigating fuel conditions. However, various circumstances
have prohibited complete enactment of those fire-hazard
reduction recommendations in these reports.
The mosaic of vegetation types occurring over the San
Francisco Bay Area in pre-settlement times consisted primarily

of perennial grasslands, coastal scrub, chaparral, oak/bay wood-
lands, and riparian woodlands. Perennial grasslands dominated

south facing slopes, while whereas oak/bay woodlands were
common on the lower portions of north facing slopes. Coastal
scrub tended to occupy the upper slopes, areas of north facing
slopes, and chaparral occurred on shallow, rocky soils, often on
south facing slopes. The native perennial grasslands were
replaced by annual grasslands in the late 18th century because
of due to the inadvertent introduction of Mediterranean annual
grass seeds during the Spanish/Mexican period.
Beginning in the late 19th century and extending into
the early 20th century, plantations of eucalypts (primarily Eucalyptus globulus), Monterey pine (Pinus radiata) and Mon-
terey cypress (Hesperocyparis macrocarpa) were established in

many parts of the Bay Area.
The potential for wildland fires to burn in urban portions of
the San Francisco Bay Area was given little recognition until the
1923 Berkeley Fire. This fire burned 65 ha, destroyed 640
structures, but fortunately no one was killed. In 1991, a fire
started above Oakland and consumed 615 ha of intermix land
and destroyed 3276 structures and 25 people died in the fire.
More recently, the Tubbs Fire, part of the complex of Wine
Country fires in 2017, burned 14,895 ha, destroyed 5636
structures, and killed 24 people, while the Camp fire in Butte
County burned 620 square kilometres and killed 86 people.
Several conditions contributed to the spread of these fires
including (1) high velocity wind during a period of low humidity

and high temperatures, (2) the existence of flammable vegeta-
tion, (3) residential neighbourhoods with many homes having

wood-shingle roofs, wood siding and wood decks, (4) areas of
steep topography and narrow roads, and (5) lack of multi-agency
preparedness for large-scale fire suppression under extreme
conditions (this included inadequate communication devices,
different fittings for fire hoses, and insufficient water storage
facilities for fighting fire in the case of the 1991 Oakland Fire).
Many fire reports and fire-hazard reduction plans were
written in response to fires and fire danger in the San Francisco

Bay Area (1923 Berkeley Fire (National Board of Fire Under-
writers 1923); 1980 Berkeley–Wildcat Fire (Blue Ribbon Fire

Prevention Committee 1982); Berkeley–Oakland Fire, 1991
(National Fire Protection Association 1992); Berkeley–Oakland
Fire, 1991 (East Bay Hills Vegetation Management Consortium

1995); and wildfire hazard in general (LSA 2010)). The prepa-
ration of repeated plans for parts of the San Francisco Bay Area indicate a problem of the implementation of fire-mitigation
planning. Common to many of these plans are recommendations
for creation of defensible space around structures, removal of
ridgetop plantations of Euclayptus globulus and Monterey pine
(known for the production of flaming embers), strategically
located fuel breaks, fuel mitigation based on topography and
wind direction during Diablo wind events, modification of
architectural features that contribute to structural fires in the
intermix zone (i.e. wood siding, shake roofs, vents allowing
flaming ember to enter attics), and improvements in interagency
preparedness and cooperation in fire suppression.
Problems confronting fire suppression became evident in the
attempts to suppress the 1991 Oakland Fire. They included a
general lack of experience of the first responders (from urban
fire departments) to suppress wildland fires, inability of
responding fire departments to communicate with each other
to coordinate fire suppression activities, the failure of fire-hose
connections used by different fire departments and CalFire to fit
existing fire plugs in the areas, and the lack of water for
firefighting in the hilly region of the fire. CalFire and local fire
departments subsequently addressed these problems.
Unfortunately, many of the proposals for fuel mitigation and
architectural changes have not been addressed. In spite of the
recommendations for fuel management put forth in more than 30
plans since 1923, no region-wide action has taken place.
Individual agencies and local Fire Safe Councils have, in part,
followed up on recommendations for fuel management on land

they administer, but often a complete adoption of recommenda-
tions has not taken place. The failure to enact all of the

recommendations of these is due to various combinations of
the following reasons:

- lack of funding;
-barriers to cooperation on the part of agencies, municipalities,
and property owners in fuel management;
-the failure of cities to enforce fuel-mitigation regulations;
-opposition of individuals and groups to vegetation manage-
ment to reduce fire hazard; and
-loss of a sense of urgency about the problem as time passes
following a destructive fire.

Fire remains a factor in the urban interface in the San Francisco Bay Area. Fuel conditions and fire weather, exacer-
bated by global warming and decreased precipitation, will contribute to an increasing fire danger. Addressing this danger
will (1) require the establishment of state oversight for hazard-abatement authority with management, fire and natural-resource staff positions to provide stability during agency transitions, set
standards to monitor local and regional fuels-management
programs, and for coordinating local, regional and state
mutual-aid fire-suppression efforts; (2) provide the leadership
to educate the public and achieve a more viable consensus about
the reality of fire hazards and need for fuel mitigation; (3)
establish and enforce clear policies about the vegetation to be
managed or preserved in both residential and wildland areas in
very high fire severity zones in the hills; (4) seek technical
improvements in linking wildland-fire science to urban-fire
science; and (5) establish zoning restrictions on construction
in high fire-hazard areas. These issues are addressed in the
following paragraphs.

Development of a state-wide or regional authority to coordi-
nate fuels management and fire suppression could contribute to

solving the fire problem in the San Francisco Bay Area. The area
is complex in terms of topography, fuels and potential fire
behaviour. Agencies and cities have their own approach to the
many issues that contribute to fire-hazard reduction; however,
fire has the potential to spread across jurisdictional boundaries.
Progress has been made in the coordination of fire suppression,
but a better coordination of fuel mitigation continues to be
required. A regional fire authority, like the California Coastal

Commission, would have the potential to address the fuel-
management problem on a regional basis. Since its inception in

1972, the California Coastal Commission was given authority
over construction and development along the coast. Its objective
has been to protect the scenic quality of the coast, maintain public
access to the coast, and prevent construction in hazardous areas
along the coast. It has state-wide responsibility for the coastal
zone and authority over counties and cities with regard to coastal

development. Another model for coordinating fire-hazard reduc-
tion across multiple jurisdictions is the Sierra Nevada Forest and

Community Initiative Regional Coordinating Council estab-
lished in 2011. Should a region-wide or state-wide authority be

established to mitigate the fuel hazard, we recommend a parcel
assessment to support the cost of fuel management. As can be
seen from the 1923 Berkeley, 1991 Oakland, 2017 Tubbs and
2018 Camp fires, the threat of fire extends beyond the interface
and intermix zone. All residents and businesses in high fire prone
areas must share in the cost of fuel mitigation.
The views of various individuals and organisations opposed
to fuel management that included removal of eucalypts and pine
trees and the use of herbicides as measures to reduce fire hazard
were not appropriately addressed early on in fuel-mitigation
planning in the East Bay Hills. A more reasoned debate about fire
safety and overall vegetation management must include the

opposition early in the fuel-mitigation planning process. Leader-
ship will be required in future to educate the public and achieve a

more viable consensus about the reality of fire hazards and need
for fuel mitigation. Good examples of the value of public
education in the development of fire-hazard reduction programs
are seen in the approach taken by the Prescribed Fire Councils in

the south-eastern United States and on federal land in the south-
west. In some cases, consensus may not be reached with some

groups or individuals, and in those situations decisions must be
made based on the best available science (Parsons 1993).
Many regulations currently exist to address fire hazard
around structures in the San Francisco Bay Area interface and
intermix zones. Enforcement of these regulations concerning fuel
mitigation has often been limited by an inadequate number of

enforcement personnel. Cities have yet to enforce clear residen-
tial fire-safety policy or provide leadership to reduce obvious

residential fire potential. Based on completed local hazard-
mitigation plans posted on agency webpages, it is apparent that

land-management agencies understand their role in reducing fire
hazard on their lands, but the cities in the interface zone have
often failed to enforce regulations. Fire-resistant architecture
standards must also be enforced to minimise fire danger to
structures. The regulations adopted by the city of Hillsborough
(https://www.hillsborough.net/DocumentCenter/View/2737/
Exhibit-H—Hillsborough-Fire-IS—Public?bidId=, accessed 12 December 2018) on the San Francisco peninsula may serve as
a viable model for such needed regulations.
Improvements in linking wildland-fire science to urban-fire
science will require the development of fire models that better
represent conditions in the urban intermix zone. We do not
believe that fuel models for predicting flame length, fire rate of
spread and fire-line intensity that have been developed for
wildland fuels are applicable to the urban interface in the San
Francisco Bay Area. Physics-based wildland fire models, such
as those developed by the US National Institute of Standards and
Technology (Mell et al. 2010), should be linked with urban-fire
models to provide an improved method for modelling intermix
fires in the San Francisco Bay Area.
This history of planning to mitigate the fire hazard in the East
Bay Hills suggests we are in, what Burton (2015) referred to as, a
Cassandra Zone:
‘ythat time period from the voicing of the first credible
warnings of foreseeable future disaster until society either
awakens to the threat and proactively mitigates against it, or
chooses to ignore such warnings and subsequently suffers the
consequences whenthe foretold disaster comesto pass.’ (p. 15)
Burton concludes:
‘Whether or not that society manages to learn from its own
history of disaster and use the power of state to mitigate
against foretold future ones is one of the definitive criteria for
determining whether such a society can be deemed to
constitute a moral community.’ (p. 15)

Maybe the Wine Country fires and the 2018 summer wild-
fires indicate that we have already passed the Cassandra Zone,

but it is hoped that California and the San Francisco Bay Area
will finally awaken to the growing need to mitigate interface and
intermix fuels around our cities.
---

## Major Fires in the Bay Area  
| Year | Name | Area Burned | Structures Lost | Fatalities |
|------|------|--------------|-----------------|-------------|
| 1923 | Berkeley Fire | 65 ha | 640 | 0 |
| 1991 | Oakland–Berkeley Hills Fire | 615 ha | 3,276 | 25 |
| 2017 | Tubbs Fire | 14,895 ha | 5,636 | 24 |
| 2018 | Camp Fire | 620 km² | 18,800 | 86 |

---

## References  
- McBride, J. R., & Kent, J. (2019). *The failure of planning to address the urban interface and intermix fire-hazard problems in the San Francisco Bay Area.* International Journal of Wildland Fire, 28(1), 1–3.  
- Additional cited sources (Blue Ribbon Committee 1982; NFPA 1992; LSA 2010; Burton 2015).  

---

`;

export default function FailureOfPlanning() {
  return (
    <div className="infra-root" style={{ minHeight: "100vh", background: "#fff" }}>
      <header
        className="infra-header"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          backdropFilter: "blur(8px)",
          background: "rgba(255,255,255,0.85)",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "16px 20px" }}>
          <h1 style={{ margin: 0, fontSize: 28 }}>
            The Failure of Planning — McBride & Kent (2019)
          </h1>
          <p style={{ margin: "6px 0 0", color: "#64748b" }}>
            Analysis of Bay Area WUI planning failures and future strategies for regional coordination.
          </p>
        </div>
      </header>

      <main style={{ maxWidth: 960, margin: "0 auto", padding: "20px" }}>
        <article className="markdown-body">
          <ReactMarkdown>{md}</ReactMarkdown>
        </article>
      </main>
    </div>
  );
}
